import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Mail, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SALES_CALENDAR_URL, SALES_CONTACT_EMAIL } from '../config/featureFlags';

export type TalkToSalesVariant =
  | 'calling'
  | 'trial_expired'
  | 'upgrade'
  | 'research_expired'
  | 'research_quota';

interface TalkToSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: TalkToSalesVariant;
  calendarUrl?: string | null;
  /** Non-dismissible — no backdrop/X close; used for expired-trial navigation lock. */
  blocking?: boolean;
  /** When false, stay on this page. Research quota / expired trial must not route to billing. */
  showBillingLink?: boolean;
}

const COPY: Record<TalkToSalesVariant, { title: string; body: string }> = {
  calling: {
    title: 'Calling is on paid plans',
    body: 'Your trial is LinkedIn-only. Book a quick call with our team to unlock sending mailboxes and AI calling.',
  },
  trial_expired: {
    title: 'Your trial has ended',
    body: 'halr is locked until you talk to sales. Book a call to reactivate your workspace.',
  },
  upgrade: {
    title: 'Talk to our team',
    body: 'We’ll walk you through pricing, onboarding, and how halr fits your outbound motion.',
  },
  research_expired: {
    title: 'Your trial has ended',
    body: 'Book a call to keep researching, launching, and sending. You can close this and stay on the page.',
  },
  research_quota: {
    title: 'Research jobs used for this period',
    body: 'Find Contacts and Halr generate share one monthly job pool. Book a call to add capacity. You can close this and stay on the page.',
  },
};

const TalkToSalesModal = ({
  isOpen,
  onClose,
  variant = 'calling',
  calendarUrl,
  blocking = false,
  showBillingLink = true,
}: TalkToSalesModalProps) => {
  const navigate = useNavigate();
  const copy = COPY[variant];
  const bookUrl = calendarUrl || SALES_CALENDAR_URL || null;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      >
        <div
          className="absolute inset-0 bg-neutral-90/50 backdrop-blur-sm"
          onClick={blocking ? undefined : onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-neutral-90/10 overflow-hidden"
          data-testid="talk-to-sales-modal"
        >
          <div className="px-6 py-5 border-b border-neutral-90/10 flex items-start justify-between gap-4">
            <div className="min-w-0 pr-2">
              <h3 className="text-lg font-semibold tracking-tight text-neutral-90">{copy.title}</h3>
              <p className="text-sm text-neutral-90/55 mt-1.5 leading-relaxed">{copy.body}</p>
            </div>
            {!blocking && (
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-neutral-90/40 hover:text-neutral-90 hover:bg-neutral-90/5 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            )}
          </div>

          <div className="px-6 py-5 flex flex-col gap-3">
            {bookUrl ? (
              <a
                href={bookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-neutral-90 text-white font-semibold text-sm shadow-sm hover:brightness-[1.03] transition-all"
              >
                <Calendar size={16} />
                Book a demo
              </a>
            ) : (
              <a
                href={`mailto:${SALES_CONTACT_EMAIL}?subject=${encodeURIComponent('halr upgrade')}`}
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-neutral-90 text-white font-semibold text-sm shadow-sm hover:brightness-[1.03] transition-all"
              >
                <Mail size={16} />
                Email {SALES_CONTACT_EMAIL}
              </a>
            )}

            {!blocking && showBillingLink && (
              <button
                onClick={() => {
                  onClose();
                  navigate('/app/billing');
                }}
                className="w-full py-2.5 px-4 rounded-xl border border-neutral-90/15 text-neutral-90 text-sm font-medium hover:bg-neutral-90/5 transition-colors"
              >
                View trial details
              </button>
            )}
            {!blocking && !showBillingLink && (
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2.5 px-4 rounded-xl border border-neutral-90/15 text-neutral-90 text-sm font-medium hover:bg-neutral-90/5 transition-colors"
              >
                Not now
              </button>
            )}
            {blocking && (
              <button
                onClick={() => navigate('/app/billing')}
                className="w-full py-2.5 px-4 rounded-xl border border-neutral-90/15 text-neutral-90 text-sm font-medium hover:bg-neutral-90/5 transition-colors"
              >
                Go to billing
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TalkToSalesModal;
